import { useRef, useState } from "react";
import { Play, Pause } from "lucide-react";
import { IMAGES } from "@/data/content";

// Martin's actual Flinders Street introduction footage
const VIDEO_SRC =
  "https://customer-assets-m6fa6gv7.emergentagent.net/job_marvellous-melbourne/artifacts/zrrqqfk3_Flinders%20Street%20Introduction.MOV";
const POSTER = IMAGES.heroFlinders;

export const MartinIntroVideo = () => {
  const ref = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [srcLoaded, setSrcLoaded] = useState(false);
  const [errored, setErrored] = useState(false);

  const toggle = async () => {
    const v = ref.current;
    if (!v) return;
    if (!srcLoaded) {
      v.src = VIDEO_SRC;
      setSrcLoaded(true);
    }
    try {
      if (v.paused) { await v.play(); setPlaying(true); }
      else { v.pause(); setPlaying(false); }
    } catch {
      setErrored(true);
    }
  };

  return (
    <section className="relative bg-[#1E4637] py-24 overflow-hidden" data-testid="intro-video-section">
      <div className="absolute inset-0 paper-texture opacity-[0.06]" />
      <div className="relative max-w-6xl mx-auto px-6 lg:px-10 grid lg:grid-cols-12 gap-10 items-center">
        <div className="lg:col-span-5 text-[#F5EFEB]">
          <div className="stamp bg-[#FDFBF7] w-fit">In Martin's own words</div>
          <h2 className="serif text-4xl md:text-5xl text-[#E7C766] mt-4 leading-tight">
            "Come and see the city I've been telling stories about for a decade."
          </h2>
          <p className="mt-5 opacity-90 leading-relaxed">
            Before you commit to a walk, let Martin tell you what to expect — the pace, the stops,
            and why he keeps coming back to the corner of Collins and Swanston at least once a week.
          </p>
          <div className="mt-6 text-xs uppercase tracking-[0.24em] text-[#E7C766]">
            1 min · Filmed in Melbourne CBD
          </div>
        </div>
        <div className="lg:col-span-7 relative">
          <div className="relative rounded-3xl overflow-hidden shadow-[0_20px_60px_rgba(0,0,0,0.35)]">
            <video
              ref={ref}
              poster={POSTER}
              preload="none"
              playsInline
              className="w-full h-[440px] object-cover bg-[#1E4637]"
              onEnded={() => setPlaying(false)}
              onError={() => setErrored(true)}
              data-testid="intro-video-el"
            />
            <button
              onClick={toggle}
              data-testid="intro-video-toggle"
              className="absolute inset-0 flex items-center justify-center bg-black/25 hover:bg-black/10 transition-colors"
              aria-label={playing ? "Pause video" : "Play video"}
            >
              <span className="w-20 h-20 rounded-full bg-[#FDFBF7] text-[#6E2638] flex items-center justify-center shadow-lg hover:scale-105 transition-transform">
                {playing ? <Pause size={28} /> : <Play size={28} className="ml-1" />}
              </span>
            </button>
            {errored && (
              <div className="absolute bottom-4 left-4 right-4 bg-[#FDFBF7]/95 text-[#6E2638] text-sm rounded-lg p-3 text-center serif italic" data-testid="video-fallback">
                Video coming soon — Martin's intro footage will appear here.
              </div>
            )}
          </div>
          <div className="absolute -bottom-5 left-6 bg-[#D4AF37] text-[#1E4637] px-4 py-2 rounded-full text-xs uppercase tracking-[0.22em] font-semibold shadow-md">
            Meet your guide
          </div>
        </div>
      </div>
    </section>
  );
};

export default MartinIntroVideo;
